import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { PermissionService } from '../services/permission.service';
import { Permission } from '../enums/permission.enum';

async function ensureAccess(router: Router, authService: AuthService): Promise<boolean> {
  const isValid = await authService.ensureValidSession();
  if (isValid) {
    return true;
  }

  if (!router.url.includes('/login')) {
    const queryParams = authService.hadUserBeforeValidation()
      ? { sessionExpired: '1' }
      : undefined;
    await router.navigate(['/login'], { queryParams, replaceUrl: true });
  }

  return false;
}

export const isLogged: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  return ensureAccess(router, authService);
};

export const hasPermission: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const permissionService = inject(PermissionService);

  if (!(await ensureAccess(router, authService))) {
    return false;
  }

  const requiredPermission: Permission | undefined = route.data?.['permission'];
  if (!requiredPermission) {
    return true;
  }

  if (!permissionService.hasPermission(requiredPermission)) {
    await router.navigate(['/home']);
    return false;
  }
  return true;
};

// La ficha de un usuario la ve su dueño o quien gestiona usuarios (el backend no restringe GET users/:id).
export const canViewUser: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const permissionService = inject(PermissionService);

  if (!(await ensureAccess(router, authService))) {
    return false;
  }

  const isOwn = Number(route.paramMap.get('id')) === authService.getUserId();
  if (isOwn || permissionService.hasPermission(Permission.ManageUsers)) {
    return true;
  }
  await router.navigate(['/home']);
  return false;
};
